/**
 * Iter-155B.0 — Trust Layer confirm dialog.
 *
 * Replaces every native window.confirm() call with a calm Shadcn
 * AlertDialog. Centralised so every confirmation across the app
 * shares the same shape:
 *   - title (warm, question form)
 *   - body (plain English, reassurance baked in)
 *   - optional secondary action (e.g. "Mark omitted instead")
 *   - destructive primary button right; Cancel left.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Delete this scene?"
 *     description="This cannot be undone. Consider marking it omitted instead."
 *     confirmLabel="Delete scene"
 *     onConfirm={handleDelete}
 *     tone="danger"
 *     testidPrefix="delete-scene"
 *   />
 */
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";

export default function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    onConfirm,
    secondaryLabel,
    onSecondary,
    tone = "default", // "default" | "danger" | "primary"
    busy = false,
    testidPrefix = "confirm",
}) {
    const toneClasses =
        tone === "danger"
            ? "bg-red-600 hover:bg-red-500 text-white"
            : tone === "primary"
                ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                : "";

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent
                data-testid={`${testidPrefix}-dialog`}
                className="max-w-md"
            >
                <AlertDialogHeader>
                    <AlertDialogTitle
                        data-testid={`${testidPrefix}-title`}
                        className="text-foreground"
                    >
                        {title}
                    </AlertDialogTitle>
                    {description ? (
                        <AlertDialogDescription
                            data-testid={`${testidPrefix}-description`}
                            className="text-muted-foreground leading-relaxed whitespace-pre-line"
                        >
                            {description}
                        </AlertDialogDescription>
                    ) : null}
                </AlertDialogHeader>
                <AlertDialogFooter className="gap-2 flex-col sm:flex-row">
                    <AlertDialogCancel
                        data-testid={`${testidPrefix}-cancel`}
                        disabled={busy}
                        className="min-tap"
                    >
                        {cancelLabel}
                    </AlertDialogCancel>
                    {secondaryLabel && onSecondary ? (
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onSecondary}
                            disabled={busy}
                            data-testid={`${testidPrefix}-secondary`}
                            className="min-tap"
                        >
                            {secondaryLabel}
                        </Button>
                    ) : null}
                    <AlertDialogAction
                        onClick={onConfirm}
                        disabled={busy}
                        data-testid={`${testidPrefix}-yes`}
                        className={`min-tap ${toneClasses}`}
                    >
                        {busy ? "Working…" : confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
